import React, { useEffect } from 'react';
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Moment } from "moment";
import { KeyResponsibilitiesConfig } from './components/KeyResponsibilitiesConfig';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { Center, Row } from '@shared/ui/atoms/StructuralLayout';
import { MountedContentCard } from '@shared/ui/atoms/MountedContentCard';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { KeyResponsibilitiesBarChart } from "./components/KeyResponsibilitiesBarChart";
import { useDispatch, useSelector } from "react-redux";
import {
  KeyResponsibilities,
  loadConfig,
  loadReportData,
  selectDate,
  selectIsLoading,
  selectSelectedKeyResponsibility,
  selectSelectedOffice,
  setDate,
  setSelectedOffice,
  setSelectedResponsibility
} from './key-responsibilities.duck';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { selectOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';

export function KeyResponsibilitiesPage(props: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const selectedKeyResponsibility = useSelector(selectSelectedKeyResponsibility);
  const office = useSelector(selectSelectedOffice);
  const date = useSelector(selectDate);
  const loading = useSelector(selectIsLoading);
  const currentUser = useSelector(selectCurrentUser);
  const rootOffices = useSelector(selectOffices);

  // Default the dropdown to the user's own office, falling back to the first office in the list.
  const usersOfficeId = currentUser?.defaultLocationId;
  const preSelectedOffice = !!usersOfficeId && rootOffices?.some((o: OfficeEntity) => o.id === usersOfficeId)
    ? usersOfficeId
    : undefined;

  useEffect(() => {
    if (office) {
      dispatch(loadConfig(office));
    }
  }, [dispatch, office]);

  useEffect(() => {
    dispatch(loadReportData({office, date, selectedKeyResponsibility}))
  }, [dispatch, office, date, selectedKeyResponsibility]);


  const onDateChange = (date: Moment) => {
    dispatch(setDate(date));
  }

  const onOfficeSelect = (office: OfficeEntity) => {
    dispatch(setSelectedOffice(office));
  }

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.key-responsib.title')}</HeadlineLarge>
      <ControlBar>
        <DateFlicker dateScale={DateScale.Month} updateGlobalDate={false} onChange={onDateChange} disabled={loading} />
        <DropDownListWrapper>
          <OfficeDropDownList onSelect={onOfficeSelect}
                              preSelectedOffice={preSelectedOffice}
                              selectDefaultOffice={true}
                              disabled={loading} />
        </DropDownListWrapper>
      </ControlBar>
      {loading ? <>
        <Center style={{marginTop: 48}}><LoadingSpinner message={'reports-page.key-responsib.loading'} hideBorder={true} /></Center>
      </> : <>
        {!!office ? <>
          <KeyResponsibilitiesBarChart />

          <Row>
            <ToggleableOutlineButton text={"reports-page.key-responsib.fire-warden"}
                                     isActive={selectedKeyResponsibility === KeyResponsibilities.fireWarden}
                                     disabled={loading}
                                     click={() => dispatch(setSelectedResponsibility(KeyResponsibilities.fireWarden))} />

            <ToggleableOutlineButton text={"reports-page.key-responsib.first-aider"}
                                     isActive={selectedKeyResponsibility === KeyResponsibilities.firstAider}
                                     disabled={loading}
                                     click={() => dispatch(setSelectedResponsibility(KeyResponsibilities.firstAider))} />
          </Row>

          <KeyResponsibilitiesConfig />
        </> : <>
          <Center style={{margin: '48px auto'}}>
            <MountedContentCard>
              <BodyRegular weight={600}>{t('reports-page.key-responsib.nothing-selected-title')}</BodyRegular>
              <BodyRegular>{t('reports-page.key-responsib.nothing-selected')}</BodyRegular>
            </MountedContentCard>
          </Center>
        </>}
      </>}

    </ReportingContainer>
  )
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding-top: 24px;
  .recharts-wrapper .recharts-cartesian-grid-horizontal line {
    stroke-opacity: 0;
  }
  .recharts-cartesian-axis-tick-line {
    display: none !important;
  }
  @media (${tablet}) {
    padding: 24px;
  }
`

const DropDownListWrapper = styled.div`
  width: 500px;
  max-width: 100%;
  @media (${tablet}) {
    width: 300px;
  }
`

const ControlBar = styled.div`
  display: flex;
  flex-direction: column;
  .weekFlicker {
    margin-bottom: 12px;
  }
  @media (${tablet}) {
    justify-content: space-between;
    flex-direction: row;
    .weekFlicker {
      margin-bottom: 0;
    }
  }
`
